import React, { useRef } from "react";
import UserReportTable1 from "../../../components/chart/UserReportTable1";
import UserReportTable from "../../../components/chart/UserReportTable";
import { knf_logo } from "../../../assets/imgs";
import html2pdf from "html2pdf.js";
import moment from "moment/moment";
import "./UserReportPage.css";

const UserReportPage = ({ data }) => {
  const pdfRef = useRef();

  const downloadPDF = () => {
    const element = pdfRef.current;
    const opt = {
      margin: 0,
      filename: `User-Report-${moment(data?.from_date).format(
        "DD-MM-YYYY",
      )}-to-${moment(data?.to_date).format("DD-MM-YYYY")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: "avoid-all" },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const w = pdf.internal.pageSize.getWidth();
        const h = pdf.internal.pageSize.getHeight();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(9);
          pdf.setTextColor(150);
          pdf.text(`Page ${i} of ${totalPages}`, w / 2, h - 10, {
            align: "center",
          });
        }
      })
      .save();
  };

  const getAlertTotals = (data = []) =>
    data.reduce(
      (acc, item) => {
        acc.processed_alerts += item.processed_alerts || 0;
        acc.assigned_alerts += item.assigned_alerts || 0;
        acc.reassigned_alerts += item.reassigned_alerts || 0;
        acc.closed_alerts += item.closed_alerts || 0;
        acc.percentage_of_closure_processed +=
          item.percentage_of_closure_processed || 0;
        acc.percentage_of_closure_total +=
          item.percentage_of_closure_total || 0;
        return acc;
      },
      {
        processed_alerts: 0,
        assigned_alerts: 0,
        reassigned_alerts: 0,
        closed_alerts: 0,
        percentage_of_closure_processed: 0,
        percentage_of_closure_total: 0,
      },
    );

  // Helper for page breaks
  const Section = ({ children, className = "" }) => (
    <div className={`report-section ${className}`}>{children}</div>
  );

  console.log("data", data);

  let { company, site } = data ?? {};

  return (
    <div className="user-report-wrapper">
      <div className="d-flex ac-je mt-3 mb-3">
        <button onClick={() => downloadPDF()} className="submit-btn">
          Download PDF
        </button>
      </div>

      <div ref={pdfRef} className="pdf-container">
        {/* HEADER SECTION */}
        <div className="d-flex align-items-center mb-4">
          <img
            src={data?.company_logo}
            alt="KNF Logo"
            style={{ height: "50px" }}
          />
          <h4 className="ms-3">
            <span style={{ color: "#852e59" }}>{company}</span> User Report for{" "}
            <span style={{ color: "#852e59" }}>{site}</span>
          </h4>
        </div>

        <table className="info-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Site Name</th>
              {data?.camera_name && <th>Camera Name</th>}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{data?.company}</td>
              <td>{data?.site}</td>
              {data?.camera_name && <td>{data?.camera_name}</td>}
            </tr>
          </tbody>
        </table>

        <div className="date-row">
          <div>From: {moment(data?.from_date).format("DD-MM-YYYY")}</div>
          <div>To: {moment(data?.to_date).format("DD-MM-YYYY")}</div>
        </div>

        <p className="fw-bold mt-3">
          Total Site Alerts for the above period - {data?.total_alert_count}
        </p>

        {/* SUMMARY TABLE */}
        <Section>
          <h3 className="mt-4 mb-2">USER WISE ALERT STATISTICS</h3>
          <table className="report-table">
            <thead>
              <tr>
                <th rowSpan="2">User Name</th>
                <th rowSpan="2">Processed Alerts</th>
                <th rowSpan="2">Assigned Alerts</th>
                <th rowSpan="2">Reassigned Alerts</th>
                <th rowSpan="2">Closed Alerts</th>
                <th colSpan="2">Percentage of Closure</th>
              </tr>
              <tr>
                <th>Out of Processed</th>
                <th>Out of Total</th>
              </tr>
            </thead>
            <tbody>
              {data?.user_performance_data?.map((item, ind) => (
                <tr key={ind}>
                  <td className="text-left">{item?.user_name}</td>
                  <td>{item?.processed_alerts}</td>
                  <td>{item?.assigned_alerts}</td>
                  <td>{item?.reassigned_alerts}</td>
                  <td>{item?.closed_alerts}</td>
                  <td>{item?.percentage_of_closure_processed}%</td>
                  <td>{item?.percentage_of_closure_total}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="fw-bold">
              <tr>
                <td>Total</td>
                <td>{data?.total_summary?.processed_alerts}</td>
                <td>{data?.total_summary?.assigned_alerts}</td>
                <td>{data?.total_summary?.reassigned_alerts}</td>
                <td>{data?.total_summary?.closed_alerts}</td>
                <td>{data?.total_summary?.percentage_of_closure_processed}%</td>
                <td>{data?.total_summary?.percentage_of_closure_total}%</td>
              </tr>
            </tfoot>
          </table>
          <p className="small mt-1 mb-0">Note:</p>
          <p className="small mx-2">
            Processed Alerts = Assigned + Reassigned + Closed Alerts
            <br />
            Percentage of Closure = (Processed Alerts or Closed Alerts) / Total
            Alerts
          </p>
        </Section>

        {/* USER BREAKUP TABLES */}
        {data?.user_performance_data?.map((item, ind) => (
          <Section key={ind} className="page-break">
            <h3 className="mt-5">USER BREAKUP - {item?.user_name}</h3>
            <table className="report-table">
              <thead>
                <tr>
                  <th rowSpan="2">Alert Type Name</th>
                  <th rowSpan="2">Processed Alerts</th>
                  <th rowSpan="2">Assigned Alerts</th>
                  <th rowSpan="2">Reassigned Alerts</th>
                  <th rowSpan="2">Closed Alerts</th>
                  <th colSpan="2">Percentage of Closure</th>
                </tr>
                <tr>
                  <th>Out of Processed</th>
                  <th>Out of Total</th>
                </tr>
              </thead>
              <tbody>
                {item?.alert_types?.map((row, index) => (
                  <tr key={index}>
                    <td className="text-left">{row?.alert_type_name}</td>
                    <td>{row?.processed_alerts}</td>
                    <td>{row?.assigned_alerts}</td>
                    <td>{row?.reassigned_alerts}</td>
                    <td>{row?.closed_alerts}</td>
                    <td>{row?.percentage_of_closure_processed}%</td>
                    <td>{row?.percentage_of_closure_total}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="small mt-1 mb-0">Note:</p>
            <p className="small mx-2">
              Processed Alerts = Assigned + Reassigned + Closed Alerts
              <br />
              Percentage of Closure = (Processed Alerts or Closed Alerts) /
              Total Alerts
            </p>
          </Section>
        ))}

        <div
          className="d-flex align-items-center justify-content-center gap-2 flex-wrap"
          style={{
            padding: "10px 16px",
            borderRadius: "12px",
            background: "#fff8fc",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            width: "fit-content",
            margin: "auto",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              fontWeight: 500,
              color: "#666",
            }}
          >
            Powered By
          </p>

          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              overflow: "hidden",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            }}
          >
            <img
              src={knf_logo}
              alt="KNF Logo"
              style={{
                width: "80%",
                height: "80%",
                objectFit: "contain",
              }}
            />
          </div>

          <p
            style={{
              margin: 0,
              fontSize: "13px",
              fontWeight: 500,
              letterSpacing: "0.5px",
              color: "#852e59",
            }}
          >
            KNF ENTERPRISES
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserReportPage;
